<template>
  <section :id="id" class="bg-light-2 pb-space-md pb-md-space-lg pt-lg-space-lg pb-lg-space-xl">
    <div class="container-lg px-0 px-lg-gutter">
      <div class="row gx-0 border border-grey bg-white">
        <!-- image column -->
        <div class="col-lg-5 p-0">
          <picture class="rfi-picture">
            <source media="(min-width: 992px)" :srcset="rfiDesktopImage" />
            <source media="(min-width: 768px)" :srcset="rfiTabletImage" />
            <img
              class="rfi-image"
              :src="rfiMobileImage"
              alt="A silhouetted person standing on a mountain faces the sun while doing the forks-up sign with their hand."
              loading="lazy"
            />
          </picture>
        </div>
        <!-- rfi column -->
        <div class="col-12 col-lg-7 px-0 bg-white">
          <form :id="`${id}_asuo-rfi`" data-cy="rfi-form" @submit.prevent="handleSubmit">
            <div
              class="py-space-md px-space-xs py-md-space-lg px-md-space-sm py-lg-space-xl px-lg-space-xxl"
            >
              <h2 v-if="title" class="h2-medium mb-space-sm">{{ title }}</h2>

              <p class="mb-space-sm fs-medium">
                Have questions about the
                <strong>{{ programName }}</strong
                >? Fill out this form and we’ll get in touch!
              </p>

              <p class="mb-space-sm fs-small">* Indicates a required field</p>

              <div :id="`${id}_form-fields`" class="mb-lg-space-md">
                <!-- first-name input field -->
                <div
                  :id="`${id}_first-name-group`"
                  class="position-relative mb-0 pb-space-sm d-flex flex-column"
                >
                  <label :for="`${id}_first-name`" class="fw-bold mb-space-xxs"
                    ><span aria-hidden="true">*</span> First name</label
                  >
                  <input
                    :id="`${id}_first-name`"
                    v-model.trim="form.firstName"
                    type="text"
                    placeholder="Enter your first name"
                    data-cy="rfi-first-name"
                    class="form-control rounded-0"
                    :class="{
                      'is-invalid': firstNameErrorMessage
                    }"
                    :aria-invalid="firstNameErrorMessage ? 'true' : undefined"
                    :aria-describedby="firstNameErrorMessage ? `${id}_first-name-error` : undefined"
                    :aria-errormessage="
                      firstNameErrorMessage ? `${id}_first-name-error` : undefined
                    "
                    @blur="handleFirstNameFieldBlur"
                  />
                  <div
                    v-if="firstNameErrorMessage"
                    :id="`${id}_first-name-error`"
                    class="invalid-feedback mb-space-xxs"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabindex="0"
                  >
                    {{ firstNameErrorMessage }}
                  </div>
                </div>
                <!-- last-name input field -->
                <div :id="`${id}_last-name-group`" class="position-relative mb-0 pb-space-sm">
                  <label :for="`${id}_last-name`" class="fw-bold mb-space-xxs"
                    ><span aria-hidden="true">*</span> Last name</label
                  >
                  <input
                    :id="`${id}_last-name`"
                    v-model.trim="form.lastName"
                    type="text"
                    placeholder="Enter your last name"
                    data-cy="rfi-last-name"
                    class="form-control rounded-0"
                    :class="{
                      'is-invalid': lastNameErrorMessage
                    }"
                    :aria-invalid="lastNameErrorMessage ? 'true' : undefined"
                    :aria-describedby="lastNameErrorMessage ? `${id}_last-name-error` : undefined"
                    :aria-errormessage="lastNameErrorMessage ? `${id}_last-name-error` : undefined"
                    @blur="handleLastNameFieldBlur"
                  />
                  <div
                    v-if="lastNameErrorMessage"
                    :id="`${id}_last-name-error`"
                    class="invalid-feedback mb-space-xxs"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabindex="0"
                  >
                    {{ lastNameErrorMessage }}
                  </div>
                </div>
                <!-- email input field -->
                <div :id="`${id}_email-group`" class="position-relative mb-0 pb-space-sm">
                  <label :for="`${id}_email`" class="fw-bold mb-space-xxs"
                    ><span aria-hidden="true">*</span> Email</label
                  >
                  <input
                    :id="`${id}_email`"
                    v-model.trim="form.email"
                    type="email"
                    placeholder="Enter your email address"
                    class="form-control rounded-0"
                    :class="{
                      'is-invalid': emailErrorMessage
                    }"
                    :aria-invalid="emailErrorMessage ? 'true' : undefined"
                    :aria-describedby="emailErrorMessage ? `${id}_email-error` : undefined"
                    :aria-errormessage="emailErrorMessage ? `${id}_email-error` : undefined"
                    data-cy="rfi-email"
                    @input="handleEmailInput()"
                    @blur="handleEmailFieldBlur"
                  />
                  <div
                    v-if="emailErrorMessage"
                    :id="`${id}_email-error`"
                    class="invalid-feedback mb-space-xxs"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabindex="0"
                  >
                    {{ emailErrorMessage }}
                  </div>
                </div>
                <!-- phone number input field -->
                <div :id="`${id}_phone-number-group`" class="position-relative mb-0 pb-space-md">
                  <label :for="`${id}_asuonline_phone_number`" class="fw-bold mb-space-xxs"
                    ><span aria-hidden="true">*</span> Phone number</label
                  >
                  <ClientOnly>
                    <PhoneInputRfi
                      :id="`${id}_asuonline_phone_number`"
                      v-model.trim="form.phone.number"
                      class="w-100"
                      :class="{ 'is-invalid': displayedPhoneError }"
                      :error="displayedPhoneError != ''"
                      :no-validator-state="true"
                      :aria-invalid="displayedPhoneError ? 'true' : undefined"
                      :aria-describedby="
                        displayedPhoneError ? `${id}_phone-number-error` : undefined
                      "
                      :aria-errormessage="
                        displayedPhoneError ? `${id}_phone-number-error` : undefined
                      "
                      @update="handlePhoneUpdate"
                      @blur="handlePhoneFieldBlur"
                    ></PhoneInputRfi>
                  </ClientOnly>
                  <div
                    v-if="displayedPhoneError"
                    :id="`${id}_phone-number-error`"
                    ref="phoneErrorRef"
                    class="invalid-feedback mb-space-xxs"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabindex="0"
                  >
                    {{ displayedPhoneError }}
                  </div>
                </div>
                <!-- military radio group -->
                <div
                  id="military-group"
                  role="radiogroup"
                  aria-labelledby="degree-military-group-label"
                  class="form-check ps-0 position-relative mb-0 pb-space-sm d-flex flex-wrap"
                >
                  <span
                    id="degree-military-group-label"
                    class="fw-bold form-check-label fs-medium w-100"
                  >
                    * Have you served in the U.S. military or are you a military dependent?
                  </span>
                  <div v-for="option in militaryOptions" :key="option.label" class="form-check">
                    <label class="form-check-label">
                      <input
                        v-model="form.isMilitary"
                        name="military"
                        class="form-check-input"
                        type="radio"
                        :value="option.value"
                        @click="handleMilitaryClick()"
                      />
                      {{ option.label }}
                    </label>
                  </div>
                </div>
                <!-- consent block -->
                <div class="fs-xs mb-space-sm mb-md-space-md gdpr-links">
                  <text-gdpr text-class="xs"></text-gdpr>
                </div>
                <input id="cq_req_id" type="hidden" name="cq_req_id" />
                <input id="cq_req_ip" type="hidden" name="cq_req_ip" />
              </div>

              <!-- submit block -->
              <div class="row gx-0">
                <div class="col-12">
                  <button
                    class="btn fs-xl fw-bold px-space-sm py-space-xs btn-secondary w-100 d-block"
                    type="submit"
                    :disabled="submitDisabled"
                    variant="secondary"
                    data-cy="rfi-submit-button"
                  >
                    Submit
                    <div v-if="showLoader" class="d-inline">
                      <span
                        class="spinner-grow spinner-grow-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span class="sr-only">Loading...</span>
                      <span
                        class="spinner-grow spinner-grow-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span class="sr-only">Loading...</span>
                      <span
                        class="spinner-grow spinner-grow-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span class="sr-only">Loading...</span>
                    </div>
                  </button>
                  <div
                    v-if="submissionError"
                    class="invalid-feedback d-block mt-space-xxs"
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                    tabindex="0"
                  >
                    {{ submissionError }}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import defaultBgImage from "~/assets/img/rfi-bg-overlay.png?url";
import rfiDesktopImage from "~/assets/img/rfi-card-image.jpg?url";
import rfiMobileImage from "~/assets/img/rfi-degree-mobile.jpg?url";
import rfiTabletImage from "~/assets/img/rfi-degree-tablet.jpg?url";
import TextGdpr from "~/components/rfi/TextGdpr.vue";
import PhoneInputRfi from "./PhoneInputRfi.vue";

interface Props {
  title?: string;
  bgImage?: string;
  career: string;
  id: string;
  // content id for the program
  programId: string;
  programName: string;
}
const props = withDefaults(defineProps<Props>(), {
  title: "Degree questions, answered",
  bgImage: defaultBgImage
});

const {
  form,
  lastNameErrorMessage,
  emailErrorMessage,
  phoneApiError,
  submissionError,
  firstNameErrorMessage,
  showLoader,
  handleEmailBlur: trackEmailBlur,
  handleEmailInput,
  handleFirstNameBlur: trackFirstNameBlur,
  handleLastNameBlur: trackLastNameBlur,
  handleMilitaryClick,
  handlePhoneBlur: trackPhoneBlur,
  handlePhoneUpdate: updatePhoneState,
  handleSubmit,
  submitDisabled
} = useRfi({
  isDegreePage: true,
  programId: props.programId,
  programName: props.programName,
  career: props.career
});

const phoneTouched = ref(false);
const phoneBlurred = ref(false);
const phoneErrorRef = ref<HTMLElement | null>(null);

const displayedPhoneError = computed(() => {
  if (phoneApiError.value) {
    return phoneApiError.value;
  }

  if (phoneBlurred.value && !form.value.phone.number) {
    return "Please enter a valid phone number and try again.";
  }

  if (phoneTouched.value && form.value.phone.number && form.value.phone.isValid === false) {
    return "Please enter a valid phone number and try again.";
  }

  return "";
});

const handleFirstNameFieldBlur = () => {
  form.value.firstName = form.value.firstName ?? "";
  trackFirstNameBlur();
};

const handleLastNameFieldBlur = () => {
  form.value.lastName = form.value.lastName ?? "";
  trackLastNameBlur();
};

const handleEmailFieldBlur = () => {
  form.value.email = form.value.email ?? "";
  trackEmailBlur();
};

const handlePhoneFieldBlur = () => {
  phoneTouched.value = true;
  phoneBlurred.value = true;
  trackPhoneBlur();
  window.setTimeout(() => {
    if (displayedPhoneError.value) {
      phoneErrorRef.value?.focus();
    }
  }, 0);
};

const handlePhoneUpdate = (payload: Parameters<typeof updatePhoneState>[0]) => {
  phoneTouched.value = phoneTouched.value || Boolean(payload.nationalNumber);
  phoneBlurred.value = false;
  updatePhoneState(payload);
};

const militaryOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];
</script>

<style lang="scss" scoped>
.form-check-input:checked {
  color: var(--rds-dark-3);
  --rds-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
  --rds-form-check-bg: var(--rds-secondary);
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
}
.form-check {
  gap: 1rem;
}
.gdpr-links {
  :deep(a) {
    text-decoration: underline;
  }
}
.ms-rfi-section {
  background-repeat: no-repeat;
  background-position: center top;
  background-size: cover;
}

.rfi-picture {
  display: block;
  height: 100%;
}

.rfi-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  min-height: 200px;
}

div.invalid-feedback {
  margin-top: 0 !important;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .rfi-image {
    min-height: 400px;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .rfi-image {
    min-height: 884px;
  }
}
</style>
